import { Component, EventEmitter, OnInit, Output, signal } from '@angular/core';
import { FormControl, FormGroup, Validators } from "@angular/forms";
import { HttpErrorResponse, HttpStatusCode } from "@angular/common/http";
import { ActivatedRoute } from "@angular/router";
import { CustomMessageService, ResponseHttpSuccess, UtilService, CustomMessageKey, OriginMessageError } from "library";
import { RequestService } from "projects/store/src/app/services/api/v1/request.service";
import { PathNavigationService } from "projects/store/src/app/services/api/v1/path-navigation.service";
import { RequestCancelReason } from "projects/store/src/app/enums";
import { SalesWorkflow } from "projects/store/src/app/enums/sales-workflow-steps.enum";
import { AuthService } from "projects/store/src/app/services/api/v1/auth.service";
import { SalesWorkflowService } from "projects/store/src/app/services/api/v1/sales-workflow.service";

const FLOW_SELLER_NOT_ALLOWED_MSG = 'Se está intentando hacer un flujo $flujo con un vendedor logueado. Esto no es permitido';

@Component({
  selector: 'app-client-identification-express',
  templateUrl: './sales-client-identification-express-kredicash.component.html',
  styles: []
})
export class SalesClientIdentificationExpressKredicashComponent implements OnInit {
  formRegister!: FormGroup;
  maxLengthCharacterFromDocument = 20;
  isSubmitted = signal<boolean>(false);
  isLoading = signal<boolean>(false);
  maxBirthDate!: Date;
  minBirthDate!: Date;
  flowCode: string;
  visibleErrorModal: boolean = false;
  errorMessageModal: string = FLOW_SELLER_NOT_ALLOWED_MSG;

  @Output() nextStepEvent = new EventEmitter();
  @Output() gotoStepEvent = new EventEmitter();

  CustomMessageKey = CustomMessageKey;

  constructor(
    private route: ActivatedRoute,
    private requestService: RequestService,
    private customMessageService: CustomMessageService,
    private utilService: UtilService,
    private pathNavigationService: PathNavigationService,
    private authService: AuthService,
    private salesWorkflowService: SalesWorkflowService
  ) {
    this.flowCode = this.route.snapshot.params['flow_code'];
  }

  ngOnInit() {
    this.initForm();
    this.initDateLimits();
    this.utilService.disablePageLoading();
    this.checkShowSellerNotAllowedModal();
  }

  private initForm() {
    this.formRegister = new FormGroup({
      document_id_number: new FormControl('', Validators.required),
      birth_date: new FormControl('', Validators.required),
    });
  }

  private initDateLimits() {
    const today = new Date();
    // Edad mínima: 18 años
    this.maxBirthDate = new Date(today.getFullYear() - 18, today.getMonth(), today.getDate());
    // Edad máxima: 100 años
    this.minBirthDate = new Date(today.getFullYear() - 100, today.getMonth(), today.getDate());
  }

  onRegister() {
    this.isSubmitted.set(true);
    if (this.formRegister.invalid) {
      return;
    }
    this.isLoading.set(true);

    const birthDate = this.formRegister.value.birth_date;
    const formattedBirthDate = this.formatDate(birthDate);

    const data = {
      document_id_number: this.formRegister.value.document_id_number,
      birth_date: formattedBirthDate
    };

    this.requestService.validateCategoryRequestByCreditExpress(this.requestService.categoryId, data).subscribe({
      next: (resp: ResponseHttpSuccess) => {
        if (resp.status === HttpStatusCode.Ok || resp.status === HttpStatusCode.Created) {
          const result = resp.body.data;
          console.log(result);

          if (result.approved && result.top_up_loan) {
            // Guardar request_id y top_up_loan en el servicio para el siguiente paso

            this.requestService.request = {
              ...this.requestService.request,
              id: result.request_id,
              top_up_loan: result.top_up_loan
            };
            this.onNextStep();
          } else {
            this.handleRequestNotApproved(RequestCancelReason.REQUEST_NOT_APPROVED);
          }
        } else {
          this.customMessageService.responseError();
        }
        this.isLoading.set(false)
      },
      error: (err: HttpErrorResponse) => {
        if (err.status === HttpStatusCode.Locked) {
          this.handleRequestLocked(err);
        } else if (err.status === HttpStatusCode.NotAcceptable) {
          const errorMessage = err.error?.message ?? 'Datos ingresados incorrectos';
          this.customMessageService.error(errorMessage, CustomMessageKey.local);
        } else {
          this.customMessageService.comunicationError();
        }
        this.isLoading.set(false);
      }
    });
  }

  private formatDate(date: Date): string {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  }

  get fr() {
    return this.formRegister.controls;
  }

  isDisabled() {
    return this.formRegister.invalid || this.formRegister.pending;
  }

  onNextStep() {
    let data = {
      requestId: this.requestService.request['id']
    }
    this.nextStepEvent.emit(data);
  }

  onGotoStep(stepCode: string) {
    this.gotoStepEvent.emit(stepCode);
  }

  private handleRequestLocked(resp: any): void {
    const messageError = resp.error?.message ?? 'Solicitud no disponible';
    const attemptedUrl = this.utilService.getCurrentUrl();
    const state = { attemptedUrl, messageError };

    this.pathNavigationService.navigateToRequestNotAvailable(this.flowCode, state);
  }

  private handleRequestNotApproved(messageError: string): void {
    const attemptedUrl = this.utilService.getCurrentUrl();
    const state = { attemptedUrl, messageError };

    this.pathNavigationService.navigateToRequestNotApproved(this.flowCode, state);
  }

  private checkShowSellerNotAllowedModal(): void {
    const isExpress = SalesWorkflow['kredicash-express'] == this.flowCode;
    const isLoggedIn = this.authService.isAuthenticated();

    this.errorMessageModal = this.errorMessageModal.replace('$flujo', `"${this.flowCode}"`);

    if (isExpress && isLoggedIn) {
      this.visibleErrorModal = true;
      this.customMessageService.responseNoticeHandler({error: this.errorMessageModal}, OriginMessageError.FRONTEND,
        {
          title: 'Acción no permitida', message: 'Estás tratando de hacer un flujo de una manera incorrecta',
          optionalMessage: 'Por favor, presiona el botón "Volver al inicio" para comenzar nuevamente',
          button: {
            label: 'Volver al inicio',
            routerLink: this.salesWorkflowService.getInitialPath(this.flowCode)?.routerLink
          }
        }
      );
    }
  }
}
